import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import FarmOutCard from '../../components/farm-outs/FarmOutCard';
import FarmOutComposer from '../../components/farm-outs/FarmOutComposer';
import { farmOutAPI, farmOutError } from '../../services/farmOutApi';

export default function AdminFarmOuts() {
  const [params] = useSearchParams();
  const reservationId = params.get('reservation');
  const offerId = params.get('offer');
  const [view, setView] = useState('available');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState('');
  const [includeDismissed, setIncludeDismissed] = useState(false);
  const [page, setPage] = useState(1);
  const list = useQuery(
    ['farm-outs', view, date, location, page, includeDismissed],
    () => farmOutAPI.list({ view, date, location, page, includeDismissed }),
    {
      enabled: !reservationId && !offerId,
      retry: false,
      refetchInterval: 30000,
    }
  );
  const detail = useQuery(
    ['farm-out-detail', offerId],
    () => farmOutAPI.detail(offerId!),
    {
      enabled: Boolean(offerId) && !reservationId,
      retry: false,
      refetchInterval: 30000,
    }
  );
  return (
    <PageLayout width="content">
      <header className="mb-6 space-y-2">
        <h1 className="text-3xl font-semibold">Farm-outs</h1>
        <p className="text-on-surface-variant">
          Offer a trip to approved operators, choose who fulfills it, and keep a
          shared handoff history.
        </p>
      </header>
      {reservationId || offerId ? (
        <div className="space-y-4">
          <Link to="/admin/farm-outs" className="text-primary underline">
            All farm-outs
          </Link>
          {reservationId ? (
            <FarmOutComposer
              key={reservationId}
              reservationId={reservationId}
            />
          ) : detail.isError ? (
            <p role="alert">{farmOutError(detail.error)}</p>
          ) : detail.data ? (
            <FarmOutCard offer={detail.data} />
          ) : (
            <p role="status">Loading handoff…</p>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <nav aria-label="Farm-out views" className="flex flex-wrap gap-2">
              {[
                ['available', 'Available'],
                ['responses', 'My proposals'],
                ['sent', 'Sent'],
                ['assigned', 'Assigned to us'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  className={view === key ? 'btn-primary' : 'btn-secondary'}
                  aria-pressed={view === key}
                  onClick={() => {
                    setView(key);
                    setPage(1);
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
            <Link to="/admin/reservations" className="text-primary underline">
              Choose a reservation to offer
            </Link>
          </div>
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <label>
              Pickup date
              <input
                type="date"
                className="input-field"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label>
              Route area
              <input
                type="search"
                className="input-field"
                placeholder="City or airport"
                maxLength={160}
                value={location}
                onChange={(e) => {
                  setLocation(e.target.value);
                  setPage(1);
                }}
              />
            </label>
            {view === 'available' && (
              <label className="py-2">
                <input
                  type="checkbox"
                  checked={includeDismissed}
                  onChange={(e) => {
                    setIncludeDismissed(e.target.checked);
                    setPage(1);
                  }}
                />{' '}
                Show dismissed offers
              </label>
            )}
          </div>
          {list.isLoading && <p role="status">Loading farm-outs…</p>}
          {list.isError && (
            <div
              role="alert"
              className="rounded-lg bg-surface-container-high p-4"
            >
              <p>{farmOutError(list.error)}</p>
              <p className="text-sm mt-2">
                New participation requires platform approval. Existing handoffs
                are available in Sent and Assigned to us.
              </p>
              <button
                className="btn-secondary mt-2"
                onClick={() => list.refetch()}
              >
                Try again
              </button>
            </div>
          )}
          {list.data && (
            <>
              {!list.data.approved && (
                <p className="mb-4 text-sm">
                  Your operator is not currently approved for new farm-outs. You
                  can still manage existing handoffs.
                </p>
              )}
              {!list.data.items.length && (
                <div className="admin-subpanel rounded-card p-8">
                  <h2 className="text-xl font-semibold">
                    {view === 'available'
                      ? 'No matching offers'
                      : view === 'responses'
                      ? 'No proposals yet'
                      : view === 'sent'
                      ? 'No sent offers yet'
                      : 'No assigned handoffs yet'}
                  </h2>
                  <p className="mt-2">
                    {view === 'sent'
                      ? 'Open an eligible reservation and choose Farm-out to publish your first offer.'
                      : view === 'responses'
                      ? 'Offers you match or counter appear here with their current response state.'
                      : view === 'assigned'
                      ? 'Confirmed handoffs appear here after an originating operator selects you.'
                      : 'Check back for new trips or adjust your date and route filters.'}
                  </p>
                </div>
              )}
              <div className="space-y-5">
                {list.data.items.map((offer) => (
                  <FarmOutCard key={offer.id} offer={offer} />
                ))}
              </div>
              {list.data.total > list.data.limit && (
                <div className="flex items-center gap-3 mt-5">
                  <button
                    className="btn-secondary"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page {page} of{' '}
                    {Math.ceil(list.data.total / list.data.limit)}
                  </span>
                  <button
                    className="btn-secondary"
                    disabled={page * list.data.limit >= list.data.total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </PageLayout>
  );
}
